'use client';

import {
  IFormsTemplate,
  deleteFormsTemplate,
  duplicateFormsTemplate,
  updateFormsTemplate,
  useFormTemplateKeys,
  useFormsTemplates,
} from '@commenda/api';
import {
  Button,
  DataTable,
  Dropdown,
  IconButton,
  Icons,
  LoadingIndicator,
} from '@commenda/kakashi';
import { FormsTemplateStatus, FormsTemplateType } from '@commenda/types';
import { printDateString } from '@commenda/utils';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useClipboard } from '../../hooks/useClipBoard';
import { useNotification } from '../../hooks/useNotification';
import { useFormEditor } from '../hooks/useFormEditor';
import { DeleteTemplateDialog } from './DeleteTemplateDialog';
import { EditableKeyCell } from './EditableKeyCell';
import { EditableStatusCell } from './EditableStatusCell';

const DEFAULT_PAGE_SIZE = 10;

interface TemplateVersionsTableProps {
  templateName: string;
  /** Opens one version in whichever editor route the host owns. */
  onOpenInEditor: (template: IFormsTemplate) => void;
  /** Row click. A host that omits it opens the editor instead. */
  onSelectVersion?: (template: IFormsTemplate) => void;
  /** Key management is admin-only, so a host that omits this gets no Key column. */
  onAddKey?: () => void;
  showKeyColumn?: boolean;
  canDuplicate?: boolean;
  canDelete?: boolean;
  /** Bumping this refetches, so a host's own modal can refresh the table after it writes. */
  refreshSignal?: number;
  header?: ReactNode;
}

export function TemplateVersionsTable({
  templateName,
  onOpenInEditor,
  onSelectVersion,
  onAddKey,
  showKeyColumn = true,
  canDuplicate = true,
  canDelete = true,
  refreshSignal,
  header,
}: TemplateVersionsTableProps) {
  const { setNotification } = useNotification();
  const { devMode } = useFormEditor();
  const { copyToClipboard } = useClipboard();

  const [offset, setOffset] = useState(0);
  const [limit] = useState(DEFAULT_PAGE_SIZE);
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<
    Record<string, string | string[]>
  >({});

  const {
    templates = [],
    isLoading,
    mutate,
    count,
  } = useFormsTemplates({
    type: [FormsTemplateType.FORM_TEMPLATE],
    searchTerm: templateName || undefined,
    limit,
    offset,
  });

  useEffect(() => {
    if (refreshSignal !== undefined) mutate();
  }, [refreshSignal]);

  const { keys: allKeys = [] } = useFormTemplateKeys({ limit: 100 });
  const keyOptions = allKeys.map((key) => ({
    label: key.name,
    value: key.key,
  }));

  const versions = useMemo(
    () =>
      [...templates]
        .filter((t) => t.name === templateName)
        .sort((a, b) => {
          if (a.status === FormsTemplateStatus.LIVE) return -1;
          if (b.status === FormsTemplateStatus.LIVE) return 1;
          return b.version.localeCompare(a.version, undefined, {
            numeric: true,
          });
        }),
    [templates, templateName]
  );

  const hasPreviousPage = offset > 0;
  const hasNextPage = offset + limit < count;

  const notify = (ok: boolean, message: string) =>
    setNotification({
      show: true,
      valid: ok,
      title: ok ? 'Success' : 'Error',
      message,
    });

  const handleDuplicate = async (template: IFormsTemplate) => {
    const result = await duplicateFormsTemplate(template.id);
    if (result.success) mutate();
    notify(
      result.success,
      result.success ? 'Template duplicated.' : 'Failed to duplicate template.'
    );
  };

  const handleDelete = async () => {
    if (!deleteTemplateId) return;
    const result = await deleteFormsTemplate(deleteTemplateId);
    if (result.success) {
      mutate();
      setDeleteTemplateId(null);
    }
    notify(
      result.success,
      result.success ? 'Template deleted.' : 'Failed to delete template.'
    );
  };

  const handleUpdateStatus = async (
    id: string,
    newStatus: FormsTemplateStatus
  ) => {
    const result = await updateFormsTemplate(id, { status: newStatus });
    if (result.success) mutate();
    notify(
      result.success,
      result.success ? 'Status updated.' : 'Failed to update status.'
    );
  };

  const handleUpdateKey = async (id: string, newKey: string | null) => {
    const result = await updateFormsTemplate(id, { key: newKey || null });
    if (result.success) mutate();
    notify(
      result.success,
      result.success ? 'Key updated.' : 'Failed to update key.'
    );
  };

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingIndicator size={48} />
      </div>
    );

  return (
    <>
      {header}
      <DataTable
        tableContainerClassName="border-0"
        tableWrapperClassName="mb-0"
        columnGroups={[
          {
            id: 'versions',
            columns: [
              ...(devMode
                ? [
                    {
                      id: 'id',
                      accessorKey: 'id',
                      header: 'ID',
                      title: 'ID',
                      cell: ({
                        row,
                      }: {
                        row: { original: IFormsTemplate };
                      }) => (
                        <div className="flex items-center justify-between gap-1 w-full">
                          <span className="txt-small-regular text-text-tertiary">
                            {row.original.id}
                          </span>
                          <Button
                            variant="tertiary"
                            size="sm"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await copyToClipboard(row.original.id);
                            }}
                          >
                            <Icons.Copy01 className="w-4 h-4" />
                          </Button>
                        </div>
                      ),
                    },
                  ]
                : []),
              {
                id: 'name',
                accessorKey: 'name',
                header: 'Name',
                title: 'Name',
                cell: ({ row }: { row: { original: IFormsTemplate } }) => (
                  <button
                    type="button"
                    className="txt-small-medium text-text-primary underline hover:text-fg-brand text-left"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenInEditor(row.original);
                    }}
                  >
                    {row.original.name}
                  </button>
                ),
              },
              {
                id: 'version',
                accessorKey: 'version',
                header: 'Version',
                title: 'Version',
                cell: ({ row }: { row: { original: IFormsTemplate } }) => (
                  <span className="txt-small-regular text-text-primary">
                    v{row.original.version}
                  </span>
                ),
              },
              {
                id: 'status',
                accessorKey: 'status',
                header: 'Status',
                title: 'Status',
                cell: ({ row }: { row: { original: IFormsTemplate } }) => (
                  <EditableStatusCell
                    id={row.original.id}
                    status={row.original.status}
                    onUpdate={handleUpdateStatus}
                  />
                ),
              },
              ...(showKeyColumn
                ? [
                    {
                      id: 'key',
                      accessorKey: 'key',
                      header: 'Key',
                      title: 'Key',
                      cell: ({ row }: { row: { original: IFormsTemplate } }) =>
                        row.original.type ===
                        FormsTemplateType.FORM_TEMPLATE ? (
                          <EditableKeyCell
                            template={row.original}
                            onUpdate={handleUpdateKey}
                            onAddKey={onAddKey}
                          />
                        ) : (
                          <>--</>
                        ),
                    },
                  ]
                : []),
              {
                id: 'createdAt',
                accessorKey: 'createdAt',
                header: 'Created At',
                title: 'Created At',
                cell: ({ row }: { row: { original: IFormsTemplate } }) => (
                  <span className="txt-small-regular text-text-secondary">
                    {printDateString(row.original.createdAt, {
                      shortMonthName: true,
                      withTime: true,
                    })}
                  </span>
                ),
              },
              {
                id: 'updatedAt',
                accessorKey: 'updatedAt',
                header: 'Updated At',
                title: 'Updated At',
                cell: ({ row }: { row: { original: IFormsTemplate } }) => (
                  <span className="txt-small-regular text-text-secondary">
                    {printDateString(row.original.updatedAt, {
                      shortMonthName: true,
                      withTime: true,
                    })}
                  </span>
                ),
              },
              {
                id: 'actions',
                accessorKey: 'actions',
                header: 'Actions',
                title: '',
                cell: ({ row }: { row: { original: IFormsTemplate } }) => (
                  <Dropdown
                    align="end"
                    menu={{
                      groups: [
                        {
                          items: [
                            ...(row.original.type ===
                            FormsTemplateType.FORM_TEMPLATE
                              ? [
                                  {
                                    label: 'Edit in Editor',
                                    onClick: () => onOpenInEditor(row.original),
                                  },
                                ]
                              : []),
                            ...(canDuplicate
                              ? [
                                  {
                                    label: 'Duplicate',
                                    onClick: () =>
                                      handleDuplicate(row.original),
                                  },
                                ]
                              : []),
                          ],
                        },
                        ...(canDelete
                          ? [
                              {
                                items: [
                                  {
                                    label: 'Delete',
                                    onClick: () =>
                                      setDeleteTemplateId(row.original.id),
                                  },
                                ],
                              },
                            ]
                          : []),
                      ],
                    }}
                  >
                    <IconButton
                      icon={<Icons.DotsVertical />}
                      size="lg"
                      aria-label="More options"
                    />
                  </Dropdown>
                ),
              },
            ],
          },
        ]}
        emptyStateConfig={{
          title: 'No versions',
          description: 'No versions found for this template.',
        }}
        tableCellClassName="border-0 h-16"
        tableHeadClassName="border-0"
        isLoading={isLoading}
        data={versions}
        onRowClick={(template) =>
          onSelectVersion
            ? onSelectVersion(template as IFormsTemplate)
            : onOpenInEditor(template as IFormsTemplate)
        }
        filterConfig={{
          filters: [
            {
              columnId: 'status',
              icon: <Icons.FileCheck02 className="size-4" />,
              type: 'MULTI_SELECT',
              label: 'Status',
              options: [
                { label: 'Draft', value: FormsTemplateStatus.DRAFT },
                { label: 'Live', value: FormsTemplateStatus.LIVE },
                { label: 'Completed', value: FormsTemplateStatus.COMPLETED },
              ],
            },
            ...(devMode && showKeyColumn
              ? [
                  {
                    columnId: 'key',
                    icon: <Icons.Key01 className="size-4" />,
                    type: 'MULTI_SELECT' as const,
                    label: 'Key',
                    options: [
                      ...keyOptions,
                      { label: 'Unlink', value: null as never },
                    ],
                  },
                ]
              : []),
          ],
          activeFilters,
          onFilterChange: setActiveFilters,
        }}
        paginationConfig={{
          isEnabled: true,
          pageSize: limit,
          type: 'CURSOR',
          showRowsPerPageSelector: true,
          rowsPerPageOptions: [10, 25, 50],
          onNextPage: () => hasNextPage && setOffset(offset + limit),
          onPreviousPage: () =>
            hasPreviousPage && setOffset(Math.max(0, offset - limit)),
          hasPreviousPage,
          hasNextPage,
        }}
      />

      <DeleteTemplateDialog
        open={Boolean(deleteTemplateId)}
        template={
          deleteTemplateId
            ? (versions.find((t) => t.id === deleteTemplateId) ?? null)
            : null
        }
        onOpenChange={(open) => {
          if (!open) setDeleteTemplateId(null);
        }}
        onConfirm={handleDelete}
      />
    </>
  );
}

export default TemplateVersionsTable;
